'use client';

import * as React from 'react';
import { useFormContext } from 'react-hook-form';

import { BackgroundType } from '@workspace/database';

import { getBackgroundsForGenerator } from '~/actions/backgrounds/get-backgrounds-for-generator';
import {
  BackgroundCard,
  SelectedBackgroundCard
} from '~/components/generator/cards/background-cards';
import {
  ItemSelectionStep,
  type ItemSelectionStepProps
} from '~/components/generator/shared/item-selection-step';
import type { ItemSortOption } from '~/components/generator/shared/item-selection-step';
import { GetBackgroundsSortBy } from '~/schemas/backgrounds/get-backgrounds-schema';
import type { CompleteGeneratorSchema } from '~/schemas/generator/complete-generator-schema';
import type { BackgroundDto } from '~/types/dtos/background-dto';

const backgroundSortOptions: ItemSortOption[] = [
  { value: GetBackgroundsSortBy.Name, label: 'Name' },
  { value: GetBackgroundsSortBy.Type, label: 'Type' },
  { value: GetBackgroundsSortBy.CreatedAt, label: 'Created Date' },
  { value: GetBackgroundsSortBy.UpdatedAt, label: 'Updated Date' }
];

const backgroundTypeLabels: Record<BackgroundType, string> = {
  [BackgroundType.STUDIO]: 'Studio',
  [BackgroundType.OUTDOOR]: 'Outdoor',
  [BackgroundType.DIGITAL]: 'Digital',
  [BackgroundType.ABSTRACT]: 'Abstract'
};

type GeneratorBackgroundsStepProps = React.HtmlHTMLAttributes<HTMLDivElement> &
  ItemSelectionStepProps<BackgroundDto, { labels: string[] }>;

export function GeneratorBackgroundsStep({
  ...props
}: Omit<
  GeneratorBackgroundsStepProps,
  | 'fetcher'
  | 'onSelectionChange'
  | 'selectedIds'
  | 'ItemCardComponent'
  | 'SelectedItemCardComponent'
  | 'getFilterDefs'
  | 'title'
  | 'description'
  | 'maxSelection'
  | 'sortOptions'
>): React.JSX.Element {
  const methods = useFormContext<CompleteGeneratorSchema>();
  const selectedBackgrounds = methods.watch('backgroundStep.backgrounds') || [];
  const [fetchedBackgrounds, setFetchedBackgrounds] = React.useState<
    BackgroundDto[]
  >([]);

  // Update selected backgrounds when fetched backgrounds change
  React.useEffect(() => {
    const currentSelectedBackgrounds =
      methods.getValues('backgroundStep.backgrounds') || [];
    if (currentSelectedBackgrounds.length > 0) {
      const updatedBackgrounds = currentSelectedBackgrounds.map(
        (selectedBackground) => {
          // If this is a fallback background (name is "Loading..."), try to find the real data
          if (selectedBackground.name === 'Loading...') {
            const realBackgroundData = fetchedBackgrounds.find(
              (bg) => bg.id === selectedBackground.id
            );
            if (realBackgroundData) {
              return realBackgroundData;
            }
          }
          return selectedBackground;
        }
      );

      // Only update if there are changes
      const hasChanges = updatedBackgrounds.some(
        (background, index) =>
          background.id !== currentSelectedBackgrounds[index]?.id ||
          background.name !== currentSelectedBackgrounds[index]?.name
      );

      if (hasChanges) {
        methods.setValue('backgroundStep.backgrounds', updatedBackgrounds, {
          shouldValidate: true,
          shouldDirty: true,
          shouldTouch: true
        });
      }
    }
  }, [fetchedBackgrounds, methods]);

  const fetcher = React.useCallback(async (args: any) => {
    const result = await getBackgroundsForGenerator({
      pageIndex: args.pageIndex,
      pageSize: args.pageSize,
      sortBy: args.sortBy,
      sortDirection: args.sortDirection,
      searchQuery: args.searchQuery,
      types: args.filters.types ?? [],
      labels: args.filters.labels ?? []
    });

    if (result?.data && !result.serverError && !result.validationErrors) {
      const { backgrounds, filteredCount, totalCount } = result.data;
      setFetchedBackgrounds((prev) => {
        const existingIds = new Set(prev.map((bg) => bg.id));
        const newBackgrounds = backgrounds.filter(
          (bg) => !existingIds.has(bg.id)
        );
        return [...prev, ...newBackgrounds];
      });
      return {
        items: backgrounds,
        filteredCount,
        totalCount,
        labels: backgrounds.flatMap((background) => background.labels)
      };
    }

    return { items: [], filteredCount: 0, totalCount: 0, labels: [] };
  }, []);

  const onSelectionChange = React.useCallback(
    (selectedIds: string[]) => {
      const newBackgrounds = selectedIds.map((id) => {
        // Find the background data from fetched backgrounds
        const backgroundData = fetchedBackgrounds.find((bg) => bg.id === id);
        if (backgroundData) {
          // Store the complete background data
          return backgroundData;
        }

        // Fallback: create a minimal background object if data not found
        // This prevents the error and allows the selection to work
        // The complete data will be fetched when needed
        console.warn(
          `Background data not immediately available for id: ${id}, using fallback`
        );
        return {
          id,
          name: 'Loading...',
          description: '',
          type: BackgroundType.STUDIO,
          labels: [],
          createdAt: new Date(),
          updatedAt: new Date()
        } as BackgroundDto;
      });
      methods.setValue('backgroundStep.backgrounds', newBackgrounds, {
        shouldValidate: true,
        shouldDirty: true,
        shouldTouch: true
      });
    },
    [methods, fetchedBackgrounds]
  );

  const getFilterDefs = React.useCallback(
    (fetcherResult: { labels: string[] } | undefined) => [
      {
        name: 'types',
        title: 'Type',
        options: Object.values(BackgroundType).map((type) => ({
          value: type,
          label: backgroundTypeLabels[type]
        }))
      }
    ],
    []
  );

  return (
    <ItemSelectionStep
      fetcher={fetcher}
      onSelectionChange={onSelectionChange}
      selectedIds={selectedBackgrounds.map((bg) => bg.id) || []}
      ItemCardComponent={BackgroundCard}
      SelectedItemCardComponent={SelectedBackgroundCard}
      getFilterDefs={getFilterDefs}
      sortOptions={backgroundSortOptions}
      title="Select your background"
      description="Select a background for your model."
      searchPlaceholder="Search by name or description..."
      maxSelection={4}
      pageSize={100}
      {...props}
    />
  );
}
